import React, { useState, useEffect, useRef } from "react";
import {
  XIcon,
  SearchIcon,
  UserPlusIcon,
  CheckIcon,
} from "./icons/FeatherIcons";
import { useAuth } from "../contexts/AuthContext";
import { createPortal } from "react-dom";
import { User } from "../types";
import { motion } from "framer-motion";
import { fallbackImage } from "../utils/getCachedImage";
import { useCachedImage } from "../hooks/useCachedImage";
import { crashlytics } from "../services/firebase";
import { CommonFunctions } from "@/utils/commonUtils";

interface InviteUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (userIds: string[]) => Promise<void>;
  activityId: string;
  alreadyInvitedIds: string[];
  participantIds: string[];
  isFull?: boolean;
}

const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
  if (e.currentTarget.src !== fallbackImage) {
    e.currentTarget.src = fallbackImage;
  }
};

interface UserInviteItemProps {
  user: User;
  isSelected: boolean;
  isDisabled?: boolean;
  onToggleSelection: (userId: string) => void;
}

const UserInviteItem: React.FC<UserInviteItemProps> = ({
  user,
  isSelected,
  isDisabled,
  onToggleSelection,
}) => {
  const profileImage = useCachedImage(
    user.profileImageUrl,
    user.username,
    user.id,
  );

  return (
    <div
      onClick={() => {
        if (!isDisabled) {
          onToggleSelection(user.id);
        }
      }}
      className={`flex items-center p-3 transition-all ${
        isDisabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
      } ${isSelected ? "bg-primary/10" : isDisabled ? "" : "hover:bg-gray-50"}`}
    >
      <img
        loading="lazy"
        onError={handleImageError}
        src={profileImage}
        alt={user.username}
        className="w-12 h-12 rounded-2xl object-cover flex-shrink-0"
      />
      <div className="ml-3 flex-1 overflow-hidden">
        <div className="flex items-center space-x-2">
          <h3 className="text-sm font-semibold text-gray-900 truncate">
            {user.username}
          </h3>
          {user.isAccountVerifiedForBadge && (
            <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs">✓</span>
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 truncate">
          {CommonFunctions.calculateAge(user.dateOfBirth)} • {user.location}
        </p>
        {user.bio && (
          <p className="text-xs text-gray-400 truncate mt-1">{user.bio}</p>
        )}
      </div>
      <div className="ml-2 flex-shrink-0">
        <div
          className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
            isSelected
              ? "bg-primary text-white"
              : "border-2 border-gray-300 bg-white"
          }`}
        >
          {isSelected && <CheckIcon className="w-4 h-4" />}
        </div>
      </div>
    </div>
  );
};

const InviteUsersModal: React.FC<InviteUsersModalProps> = ({
  isOpen,
  onClose,
  onInvite,
  activityId,
  alreadyInvitedIds,
  participantIds,
  isFull,
}) => {
  const { currentUser, searchUsers } = useAuth();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState<any>(null);
  const [searchTimeoutId, setSearchTimeoutId] = useState<NodeJS.Timeout | null>(
    null,
  );
  const isFullActivity = Boolean(isFull);

  // Reset state when modal closes
  useEffect(() => {
    if (isOpen) {
      loadInitialUsers();
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else {
      resetState();
    }
  }, [isOpen]);

  // Handle search query changes with debouncing
  useEffect(() => {
    if (searchTimeoutId) {
      clearTimeout(searchTimeoutId);
    }

    const timeoutId = setTimeout(() => {
      handleSearch();
    }, 300);

    setSearchTimeoutId(timeoutId);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [searchQuery]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, onClose]);

  // Infinite scroll
  useEffect(() => {
    const trigger = loadMoreTriggerRef.current;
    if (!trigger) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isLoadingMore) {
          loadMoreUsers();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(trigger);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, lastDoc, searchQuery]);

  const resetState = () => {
    setSearchQuery("");
    setUsers([]);
    setSelectedUserIds([]);
    setLastDoc(null);
    setHasMore(true);
    setIsLoading(false);
    setIsLoadingMore(false);
  };

  const filterUsers = (userList: User[]) => {
    return userList.filter(
      (user) =>
        user.id !== currentUser?.id &&
        !participantIds.includes(user.id) &&
        !alreadyInvitedIds.includes(user.id),
    );
  };

  const loadInitialUsers = async () => {
    if (!currentUser) return;

    setIsLoading(true);
    try {
      const result = await searchUsers("", null, 20);
      const filteredUsers = filterUsers(result.users);
      setUsers(filteredUsers);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (error) {
      crashlytics.logError(
        `Error loading initial users: ${JSON.stringify(error?.message)}`,
        "InviteUsersModal.tsx",
        error,
      );
      setUsers([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!currentUser) return;

    setIsLoading(true);
    setLastDoc(null);
    setHasMore(true);

    try {
      const result = await searchUsers(searchQuery, null, 20);
      const filteredUsers = filterUsers(result.users);
      setUsers(filteredUsers);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (error) {
      crashlytics.logError(
        `Error searching users: ${JSON.stringify(error?.message)}`,
        "InviteUsersModal.tsx",
        error,
      );
      setUsers([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreUsers = async () => {
    if (!hasMore || isLoadingMore || !lastDoc || !currentUser) return;

    setIsLoadingMore(true);
    try {
      const result = await searchUsers(searchQuery, lastDoc, 20);
      const filteredUsers = filterUsers(result.users);
      setUsers((prev) => [...prev, ...filteredUsers]);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (error) {
      crashlytics.logError(
        `Error loading more users: ${JSON.stringify(error?.message)}`,
        "InviteUsersModal.tsx",
        error,
      );
    } finally {
      setIsLoadingMore(false);
    }
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const handleSendInvites = async () => {
    if (selectedUserIds.length === 0 || isFullActivity) return;

    setIsSending(true);
    try {
      await onInvite(selectedUserIds);
      onClose();
    } catch (error) {
      crashlytics.logError(
        `Error sending invites: ${JSON.stringify(error?.message)}`,
        "InviteUsersModal.tsx",
        error,
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-start justify-center z-[100] p-4 pt-20"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
          <h2 className="text-lg font-bold text-gray-900">Invite Users</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
          >
            <XIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Search Input */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              autoComplete="off"
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by username, location, or interests..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>

        {isFullActivity && (
          <div className="px-4 py-3 bg-yellow-50 border-b border-yellow-200">
            <p className="text-sm text-yellow-800 font-medium">
              This activity is full. You cannot send new invites.
            </p>
          </div>
        )}

        {/* Selected Users Count */}
        {selectedUserIds.length > 0 && (
          <div className="px-4 py-2 bg-primary/5 border-b border-primary/10">
            <p className="text-sm text-primary font-medium">
              {selectedUserIds.length} user
              {selectedUserIds.length > 1 ? "s" : ""} selected
            </p>
          </div>
        )}

        {/* Search Results */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && users.length === 0 ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : users.length > 0 ? (
            <>
              <div className="py-2">
                {users.map((user) => (
                  <UserInviteItem
                    key={user.id}
                    user={user}
                    isSelected={selectedUserIds.includes(user.id)}
                    isDisabled={isFullActivity}
                    onToggleSelection={toggleUserSelection}
                  />
                ))}
              </div>

              {/* Infinite scroll trigger */}
              <div ref={loadMoreTriggerRef} className="w-full py-2">
                {isLoadingMore && (
                  <div className="flex items-center justify-center py-4">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full"
                    />
                    <span className="ml-2 text-sm text-gray-500">
                      Loading more users...
                    </span>
                  </div>
                )}

                {!hasMore && users.length > 0 && (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-400">
                      You've seen all available users!
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : searchQuery.trim() !== "" ? (
            <div className="py-12 px-4 text-center">
              <SearchIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">
                No users found matching "{searchQuery}"
              </p>
              <p className="text-gray-400 text-xs mt-1">
                Users already in the activity or invited are not shown
              </p>
            </div>
          ) : (
            <div className="py-12 px-4 text-center">
              <UserPlusIcon className="w-13 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">
                Search for users to invite
              </p>
              <p className="text-gray-400 text-xs mt-1">
                Start typing to find users by username, location, or interests
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-white">
          <button
            onClick={handleSendInvites}
            disabled={
              selectedUserIds.length === 0 || isSending || isFullActivity
            }
            className={`w-full py-3 px-4 rounded-lg font-semibold transition-all ${
              selectedUserIds.length > 0 && !isSending && !isFullActivity
                ? "bg-primary text-white hover:bg-primary/90"
                : "bg-gray-200 text-gray-500 cursor-not-allowed"
            }`}
          >
            {isSending ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Sending invites...
              </div>
            ) : (
              `Invite ${selectedUserIds.length > 0 ? `${selectedUserIds.length} user${selectedUserIds.length > 1 ? "s" : ""}` : "users"}`
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default InviteUsersModal;
